import Highcharts from "highcharts";
import HighchartsMore from "highcharts/highcharts-more";
import { FunctionComponent, useEffect, useMemo, useState } from "react";
// import Stock from "highcharts/modules/stock";
// import NoDataToDisplay from "highcharts/modules/no-data-to-display";
// import SolidGauge from "highcharts/modules/solid-gauge";
import { Concern, useExplore } from "@/components/explore/ExploreContext";
import { PlayDetail } from "@/components/playlist/PlayDetail";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useSessionData } from "@/stores/SessionDataContext";
import HighchartsReact from "highcharts-react-official";
import Heatmap from "highcharts/modules/heatmap";

if (typeof Highcharts === "object" && typeof window !== "undefined") {
  HighchartsMore(Highcharts);
  // Stock(Highcharts);
  // NoDataToDisplay(Highcharts);
  // SolidGauge(Highcharts);
  Heatmap(Highcharts);
}

export function getMinutesSinceMidnightInZone(
  now: Date,
  timeZone?: string
): number {
  const localFallback = () => now.getHours() * 60 + now.getMinutes();
  if (!timeZone?.trim()) {
    return localFallback();
  }
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timeZone.trim(),
      hour: "numeric",
      minute: "numeric",
      hour12: false,
    }).formatToParts(now);
    const hourRaw = parts.find((p) => p.type === "hour")?.value;
    const minuteRaw = parts.find((p) => p.type === "minute")?.value;
    if (hourRaw === undefined || minuteRaw === undefined) {
      return localFallback();
    }
    let hour = parseInt(hourRaw, 10);
    const minute = parseInt(minuteRaw, 10);
    if (Number.isNaN(hour) || Number.isNaN(minute)) {
      return localFallback();
    }
    if (hour === 24) {
      hour = 0;
    }
    return hour * 60 + minute;
  } catch {
    return localFallback();
  }
}

export function timeLabelToMinutes(time: string): number {
  const match = /^(\d+):(\d+)(?:\s+\+(\d+))?$/.exec(time.trim());
  if (!match) {
    return Number.NaN;
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const day = match[3] ? Number(match[3]) : 0;
  return day * 24 * 60 + hours * 60 + minutes;
}

export function findClosestTimeIndex(
  times: string[],
  timeZone?: string
): number {
  const now = new Date();
  const currentMinutes = getMinutesSinceMidnightInZone(now, timeZone);

  let bestIndex = 0;
  let bestDiff = Infinity;
  times.forEach((time, index) => {
    const minutes = timeLabelToMinutes(time);
    if (Number.isNaN(minutes)) {
      return;
    }
    const diff = Math.abs(minutes - currentMinutes);
    if (diff < bestDiff || (diff === bestDiff && index > bestIndex)) {
      bestDiff = diff;
      bestIndex = index;
    }
  });

  return bestIndex;
}

function ConcernDisplay({ concern }: { concern: Concern }) {
  return <PlayDetail playId={concern.play_id} />;
}

export type TooltipFormatter = (params: {
  zoneName: string;
  timeLabel: string;
  value: number;
  measure: string;
  suffixText: string;
}) => string;

interface HeatMapViewProps {
  measure: string;
  height: number;
  customTooltipFormatter?: TooltipFormatter;
  onCellClick?: (
    zoneId: number,
    interval: number,
    zoneName: string,
    timeLabel: string,
    zoneIds?: number[]
  ) => void;
}

export const HeatMapView: FunctionComponent<HeatMapViewProps> = ({
  measure,
  height,
  customTooltipFormatter,
  onCellClick,
}) => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [zoneBreakdown, setZoneBreakdown] = useState<null | string>(null);
  const [alert, setAlert] = useState<null | Concern>(null);
  const [concernThreshold, setConcernThreshold] = useState<number | null>(null);

  const { zones, concerns, times, zoneType } = useExplore();
  const { airport_config } = useSessionData();
  const closestIndex = findClosestTimeIndex(times, airport_config.timezone);

  const legendText = {
    pax: "Passengers",
    flight: "Flights",
    bag: "Bags",
    bags: "Bags",
  }[measure];

  const suffixText =
    {
      pax: "Passengers",
      flight: "Flights",
      bag: "Bags",
      bags: "Bags",
    }[measure] ?? "Passengers";

  const conLookup = useMemo(() => {
    const lookup = {};
    if (concerns && concerns.length > 0) {
      concerns.forEach((c, index) => {
        lookup[`${c.zone_index}-${c.time_index}`] = index;
      });
    }

    return lookup;
  }, [concerns]);

  const data = useMemo(() => {
    const result = [];
    zones.forEach((zone, yIndex) => {
      for (let xIndex = 0; xIndex < times.length; xIndex++) {
        const value = zone.values[xIndex] ?? 0;
        const conKey = `${yIndex}-${xIndex}`;
        if (conLookup.hasOwnProperty(conKey)) {
          result.push({
            x: xIndex,
            y: yIndex,
            value: value,
            conIndex: conLookup[conKey],
            color: "#8b0000",
          });
        } else {
          result.push([xIndex, yIndex, value]);
        }
      }
    });

    return result;
  }, [zones, times, conLookup]);

  useEffect(() => {
    if (concerns && concerns.length > 0) {
      setConcernThreshold(concerns[0].threshold);
    } else {
      setConcernThreshold(null);
    }
  }, [concerns]);

  // @ts-ignore
  // @ts-ignore
  const options: Highcharts.Options = {
    credits: {
      enabled: false,
    },
    chart: {
      styledMode: true,
      height: height,
    },
    title: {
      text: undefined,
    },
    legend: {
      enabled: true,
      title: {
        text: legendText ?? "Passengers",
      },
    },
    tooltip: {
      formatter: function () {
        const x = this.point.x;
        const y = this.point.y;
        const zone = zones[y];
        const value = zone.values[x];

        if (customTooltipFormatter) {
          return customTooltipFormatter({
            zoneName: zone.name,
            timeLabel: times[x],
            value,
            measure,
            suffixText,
          });
        }

        return `<b>${zone.name} @ ${times[x]}</b><br/><b>Total: ${value} ${suffixText}</b>`;
      },
      useHTML: true,
    },
    series: [
      {
        type: "heatmap",
        name: "Traffic",
        borderWidth: 1,
        data: data,
        dataLabels: {
          enabled: false,
        },
        turboThreshold: 80000,
        events: {
          click: function (event) {
            const x = event.point.x;
            const y = event.point.y;
            const zone = zones[y];

            if (event.point.color === "#8b0000") {
              // @ts-ignore
              setAlert(concerns[event.point.conIndex]);
              let breakdown = "";
              if (zone.notes?.[x]) {
                breakdown = zone.notes[x];
              }
              setTitle(`Breakdown: ${zone.name} @ ${times[x]}`);
              setZoneBreakdown(breakdown);
              setOpen(true);
            } else {
              if (zone.id && onCellClick) {
                onCellClick(zone.id, x, zone.name, times[x], zone.zone_ids);
              }
            }
          },
        },
      },
    ],
    xAxis: {
      type: "category",
      categories: times,
      min: 0,
      max: Math.max(times.length - 1, 0),
      endOnTick: false,
      startOnTick: false,
      tickmarkPlacement: "on",
      plotBands: [
        {
          from: closestIndex + 0.5,
          to: closestIndex + 1.5,
          zIndex: 10,
        },
      ],
    },
    yAxis: {
      categories: zones.map((zone) => zone.name),
      title: null,
      reversed: zoneType === "door", // Reverse y-axis for doors to show CSV order top-to-bottom
    },
    colorAxis: {
      min: 0,
      max: concernThreshold,
      minColor: "#1A237E", // Dark Blue
      maxColor: "#FF8C00", // Medium-Dark Orange
    },
  };

  return (
    <div className={"w-full highcharts-dark"}>
      <HighchartsReact
        highcharts={Highcharts}
        constructorType={"chart"}
        options={options}
      />
      <div
        className={
          "text-center text-sm text-slate-300 mb-4 flex items-center justify-center"
        }
      >
        <div className={"mr-2 w-3 h-6 bg-red-800"} /> Pax Exceeds Set Threshold
      </div>
      <Dialog open={open} onOpenChange={(val) => setOpen(val)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[calc(100vh-250px)]">
            {zoneBreakdown !== "" && (
              <div className={" prose mt-2 mb-2"}>
                <pre>{zoneBreakdown}</pre>
              </div>
            )}
            {alert && <ConcernDisplay concern={alert} />}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};
